import { useEffect, useState } from 'react';
import { Search, X, ChevronDown, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button.jsx';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu.jsx';
import { listCarriers } from '@/services/quotes.service.js';
import { cn } from '@/lib/utils.js';

// Pipeline kanban toolbar (spec §3.6). All controls write through to
// URL query state via usePipelineFilters.
//
// 2026-05-20 (Vision §V2 fix #5): added carrier multi-select, referral-
// partner multi-select, aging buckets, and a custom date range picker.
// Carrier + partner lists are aggregated from the visible pipeline
// items via distinctCarriers/distinctPartners (passed in via props) —
// the filter directory is whatever's currently on the broker's board,
// not a static carrier registry. URL state persisted via the same hook.

// Mine is the deals whose policy names the signed-in person as producer; Unassigned, the deals whose policy names
// nobody (the server decides both, deals-door-1 T19). The producer is not the Deals CRM producer lookup: that is the
// placement partner ("Placed through"), and it decides no scope.
const SCOPE_OPTS = [
  { value: 'all', label: 'All' },
  { value: 'mine', label: 'Mine' },
  { value: 'unassigned', label: 'Unassigned' },
];

const RANGE_OPTS = [
  { value: 'any', label: 'Any date' },
  { value: '30', label: 'Next 30d' },
  { value: '60', label: 'Next 60d' },
  { value: '100', label: 'Next 100d' },
  { value: 'custom', label: 'Custom range…' },
];

const AGING_OPTS = [
  { value: 'any', label: 'Any age' },
  { value: '0-3', label: '0-3d' },
  { value: '4-7', label: '4-7d' },
  { value: '8-14', label: '8-14d' },
  { value: '15-30', label: '15-30d' },
  { value: '30+', label: '30+d' },
];

const SORT_OPTS = [
  { value: 'urgency', label: 'Urgency' },
  { value: 'name', label: 'Name' },
  { value: 'activity', label: 'Last activity' },
];

const selectClass = cn(
  'h-9 rounded-card border border-border bg-bg px-2 text-xs text-text',
  'focus:border-accent focus:outline-none'
);

export default function BoardFilters({
  filters,
  update,
  reset,
  totalCount,
  // partners stay derived from visible pipeline (small set, item-shaped).
  // carriers are sourced from the canonical Vendors module via
  // /api/tracon/carriers (Vision §V2 fix #1, 2026-05-20) — this component
  // fetches the list itself so other surfaces don't have to thread it.
  partners = [],
  // aircraft facet, aggregated from the visible board: make/model
  // (distinctAircraft) + ICAO type/category buckets (distinctAircraftTypes).
  aircraft = [],
  aircraftTypes = [],
  // mineOff: a sentence when there is no person to be Mine for (the shared login, or /auth/me unread; lib/mine.js).
  mineOff = '',
}) {
  const [carrierList, setCarrierList] = useState([]);

  useEffect(() => {
    let cancelled = false;
    listCarriers()
      .then((r) => { if (!cancelled) setCarrierList(r?.carriers || []); })
      .catch(() => { if (!cancelled) setCarrierList([]); });
    return () => { cancelled = true; };
  }, []);

  // carrierList shape from backend: { id, vendor_name, underwriter, label }.
  // Filter persists Vendor.id values; the label is what the broker sees.
  const carrierById = new Map(carrierList.map((c) => [c.id, c]));

  const customRangeActive = !!(filters.from || filters.to);
  const dirty =
    filters.scope !== 'all' ||
    filters.range !== 'any' ||
    filters.sort !== 'urgency' ||
    filters.q !== '' ||
    filters.closed ||
    filters.carriers.length > 0 ||
    filters.partners.length > 0 ||
    filters.aircraft.length > 0 ||
    filters.aircraft_type.length > 0 ||
    filters.aging !== 'any' ||
    customRangeActive;

  // When the broker picks "Custom range…" from the date dropdown, we
  // show two date inputs and clear the canned `range`. Picking another
  // canned range clears `from`/`to`.
  function handleRangeChange(value) {
    if (value === 'custom') {
      // Default custom range: today → today + 60d so the broker isn't
      // staring at empty inputs. They can immediately edit.
      const today = new Date();
      const sixty = new Date(today.getTime() + 60 * 86400000);
      update({
        range: 'any',
        from: today.toISOString().slice(0, 10),
        to: sixty.toISOString().slice(0, 10),
      });
    } else {
      update({ range: value, from: '', to: '' });
    }
  }

  function toggleListEntry(currentList, value) {
    if (currentList.includes(value)) {
      return currentList.filter((v) => v !== value);
    }
    return [...currentList, value];
  }

  return (
    <div className="sticky top-0 z-20 flex flex-wrap items-center gap-2 border-b border-border bg-bg/95 px-4 py-2 backdrop-blur md:px-6">
      <div className="inline-flex overflow-hidden rounded-card border border-border">
        {SCOPE_OPTS.map((opt) => (
          <button
            key={opt.value}
            type="button"
            disabled={opt.value === 'mine' && !!mineOff}
            title={opt.value === 'mine' && mineOff ? mineOff : undefined}
            onClick={() => update({ scope: opt.value })}
            className={cn(
              'px-3 py-1.5 text-xs font-medium transition disabled:cursor-not-allowed disabled:opacity-50',
              filters.scope === opt.value
                ? 'bg-accent-soft text-accent'
                : 'text-text-muted hover:bg-border/30 hover:text-text'
            )}
          >
            {opt.label}
          </button>
        ))}
      </div>
      {mineOff ? <span className="text-xs text-text-dim">{mineOff}</span> : null}

      <select
        value={customRangeActive ? 'custom' : filters.range}
        onChange={(e) => handleRangeChange(e.target.value)}
        className={selectClass}
        aria-label="Date range"
      >
        {RANGE_OPTS.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>

      {customRangeActive ? (
        <div className="inline-flex items-center gap-1 rounded-card border border-border bg-bg px-2 py-1">
          <Calendar size={12} className="text-text-dim" />
          <input
            type="date"
            value={filters.from}
            onChange={(e) => update({ from: e.target.value })}
            className="bg-transparent text-xs text-text focus:outline-none"
            aria-label="From date"
          />
          <span className="text-[10px] text-text-dim">→</span>
          <input
            type="date"
            value={filters.to}
            onChange={(e) => update({ to: e.target.value })}
            className="bg-transparent text-xs text-text focus:outline-none"
            aria-label="To date"
          />
        </div>
      ) : null}

      {/* Carrier multi-select */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button type="button" className={cn(selectClass, 'inline-flex items-center gap-1 px-3')}>
            {filters.carriers.length > 0
              ? `Carrier · ${filters.carriers.length}`
              : 'Carrier'}
            <ChevronDown size={12} className="text-text-dim" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          sideOffset={6}
          collisionPadding={16}
          className="max-h-[320px] overflow-y-auto"
        >
          <DropdownMenuLabel>Filter by carrier</DropdownMenuLabel>
          {carrierList.length === 0 ? (
            <div className="px-2 py-3 text-[11px] text-text-dim">
              Loading carriers from Vendors module…
            </div>
          ) : (
            carrierList.map((c) => (
              <DropdownMenuCheckboxItem
                key={c.id}
                checked={filters.carriers.includes(c.id)}
                onCheckedChange={() =>
                  update({ carriers: toggleListEntry(filters.carriers, c.id) })
                }
                onSelect={(e) => e.preventDefault()}
              >
                {c.label}
              </DropdownMenuCheckboxItem>
            ))
          )}
          {filters.carriers.length > 0 ? (
            <>
              <DropdownMenuSeparator />
              <button
                type="button"
                onClick={() => update({ carriers: [] })}
                className="block w-full rounded-card px-2 py-1.5 text-left text-xs text-text-muted hover:bg-border/30 hover:text-text"
              >
                Clear carriers
              </button>
            </>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Referral partner multi-select */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button type="button" className={cn(selectClass, 'inline-flex items-center gap-1 px-3')}>
            {filters.partners.length > 0
              ? `Partner · ${filters.partners.length}`
              : 'Partner'}
            <ChevronDown size={12} className="text-text-dim" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          sideOffset={6}
          collisionPadding={16}
          className="max-h-[320px] overflow-y-auto"
        >
          <DropdownMenuLabel>Filter by partner</DropdownMenuLabel>
          {partners.length === 0 ? (
            <div className="px-2 py-3 text-[11px] text-text-dim">
              No referral attribution on the visible board.
            </div>
          ) : (
            partners.map((p) => (
              <DropdownMenuCheckboxItem
                key={p.id}
                checked={filters.partners.includes(p.id)}
                onCheckedChange={() =>
                  update({ partners: toggleListEntry(filters.partners, p.id) })
                }
                onSelect={(e) => e.preventDefault()}
              >
                {p.label}
              </DropdownMenuCheckboxItem>
            ))
          )}
          {filters.partners.length > 0 ? (
            <>
              <DropdownMenuSeparator />
              <button
                type="button"
                onClick={() => update({ partners: [] })}
                className="block w-full rounded-card px-2 py-1.5 text-left text-xs text-text-muted hover:bg-border/30 hover:text-text"
              >
                Clear partners
              </button>
            </>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Aircraft multi-select (NEW) — two facets from the visible board:
          ICAO type/category buckets + make/model. Union within the facet. */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button type="button" className={cn(selectClass, 'inline-flex items-center gap-1 px-3')}>
            {(() => {
              const n = filters.aircraft.length + filters.aircraft_type.length;
              return n > 0 ? `Aircraft · ${n}` : 'Aircraft';
            })()}
            <ChevronDown size={12} className="text-text-dim" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          sideOffset={6}
          collisionPadding={16}
          className="max-h-[360px] overflow-y-auto"
        >
          <DropdownMenuLabel>By type (ICAO)</DropdownMenuLabel>
          {aircraftTypes.length === 0 ? (
            <div className="px-2 py-2 text-[11px] text-text-dim">No mapped types on the board.</div>
          ) : (
            aircraftTypes.map((t) => (
              <DropdownMenuCheckboxItem
                key={`ty-${t.id}`}
                checked={filters.aircraft_type.includes(t.id)}
                onCheckedChange={() =>
                  update({ aircraft_type: toggleListEntry(filters.aircraft_type, t.id) })
                }
                onSelect={(e) => e.preventDefault()}
              >
                <span className="font-mono text-[10px] text-text-dim">{t.id}</span>
                <span className="ml-1.5">{t.label}</span>
              </DropdownMenuCheckboxItem>
            ))
          )}
          <DropdownMenuSeparator />
          <DropdownMenuLabel>By make/model</DropdownMenuLabel>
          {aircraft.length === 0 ? (
            <div className="px-2 py-2 text-[11px] text-text-dim">No make/model on the board.</div>
          ) : (
            aircraft.map((a) => (
              <DropdownMenuCheckboxItem
                key={`mm-${a.id}`}
                checked={filters.aircraft.includes(a.id)}
                onCheckedChange={() =>
                  update({ aircraft: toggleListEntry(filters.aircraft, a.id) })
                }
                onSelect={(e) => e.preventDefault()}
              >
                {a.label}
              </DropdownMenuCheckboxItem>
            ))
          )}
          {filters.aircraft.length > 0 || filters.aircraft_type.length > 0 ? (
            <>
              <DropdownMenuSeparator />
              <button
                type="button"
                onClick={() => update({ aircraft: [], aircraft_type: [] })}
                className="block w-full rounded-card px-2 py-1.5 text-left text-xs text-text-muted hover:bg-border/30 hover:text-text"
              >
                Clear aircraft
              </button>
            </>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>

      <select
        value={filters.aging}
        onChange={(e) => update({ aging: e.target.value })}
        className={selectClass}
        aria-label="Aging"
      >
        {AGING_OPTS.map((o) => (
          <option key={o.value} value={o.value}>
            Aging: {o.label}
          </option>
        ))}
      </select>

      <select
        value={filters.sort}
        onChange={(e) => update({ sort: e.target.value })}
        className={selectClass}
        aria-label="Sort by"
      >
        {SORT_OPTS.map((o) => (
          <option key={o.value} value={o.value}>
            Sort: {o.label}
          </option>
        ))}
      </select>

      <label className="relative flex flex-1 items-center md:min-w-[180px] md:flex-initial">
        <Search
          size={14}
          strokeWidth={1.5}
          className="pointer-events-none absolute left-2 text-text-dim"
        />
        <input
          type="search"
          value={filters.q}
          onChange={(e) => update({ q: e.target.value })}
          placeholder="Search board…"
          className={cn(
            'h-9 w-full rounded-card border border-border bg-bg pl-7 pr-2 text-xs text-text',
            'placeholder:text-text-dim',
            'focus:border-accent focus:outline-none'
          )}
        />
      </label>

      <label className="inline-flex cursor-pointer items-center gap-1.5 text-xs text-text-muted">
        <input
          type="checkbox"
          checked={filters.closed}
          onChange={(e) => update({ closed: e.target.checked })}
          className="h-4 w-4 accent-accent"
        />
        Show closed
      </label>

      {totalCount != null ? (
        <span className="ml-auto text-[10px] font-mono text-text-dim">{totalCount} deals</span>
      ) : null}

      {dirty ? (
        <Button size="sm" variant="ghost" onClick={reset} className="gap-1">
          <X size={12} strokeWidth={1.5} />
          <span className="text-xs">Reset</span>
        </Button>
      ) : null}
    </div>
  );
}
